import { create } from "zustand";

import { api } from "../api/client";
import type { StreamGrant } from "../api/types";

type AudioStatus =
  | "idle"
  | "loading"
  | "playing"
  | "ready_for_tap"
  | "stopped"
  | "error";

type PlayOptions = {
  title: string;
  grant?: () => Promise<StreamGrant>;
  streamUrl?: string;
  fallbackStreamUrl?: string;
  contentType?: string;
  contentId?: string;
  episodeId?: string;
  seriesId?: string;
  startAtSeconds?: number;
};

type AudioState = {
  status: AudioStatus;
  currentTitle: string;
  message: string;
  announce: (message: string) => void;
  play: (options: PlayOptions) => Promise<void>;
  resumePrepared: () => Promise<void>;
  stopAll: () => void;
};

class StreamAuthorizationError extends Error {
  constructor(readonly status: number) {
    super(`stream authorization failed: ${status}`);
  }
}

const audio = new Audio();
let clientSequence = 0;
let activePlayerSessionId = createPlayerSessionId();
let activePlayRequestId = 0;
let activeOptions: PlayOptions | null = null;
let pendingPlayKey: string | null = null;
let recoveringPlayRequestId: number | null = null;
let suppressPauseRecovery = false;
let playbackErrorRecoveryCount = 0;

const STREAM_PREPARE_ATTEMPTS = 3;
const AUDIO_PLAY_ATTEMPTS = 2;
const PLAYBACK_ERROR_RECOVERY_LIMIT = 2;
const RETRY_DELAY_MS = 650;

function createPlayerSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `frontend-phase0-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isAutoplayBlocked(error: unknown): boolean {
  return error instanceof DOMException ||
    error instanceof Error ||
    (typeof error === "object" && error !== null && "name" in error)
    ? String((error as { name?: unknown }).name) === "NotAllowedError"
    : false;
}

function isRetryablePlaybackStartError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return true;
  }
  return ["AbortError", "NetworkError", "NotSupportedError"].includes(
    error.name,
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function playRequestKey(options: PlayOptions): string {
  return [
    options.contentType ?? "media_episode",
    options.episodeId ??
      options.contentId ??
      options.streamUrl ??
      options.title,
  ].join(":");
}

function isCurrentPlayRequest(playRequestId: number): boolean {
  return activePlayRequestId === playRequestId;
}

function readablePosition(): number {
  return Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
}

function saveProgress(options: PlayOptions): void {
  if (!options.episodeId) {
    return;
  }

  clientSequence += 1;
  void api
    .progress({
      content_type: options.contentType ?? "media_episode",
      series_id: options.seriesId,
      episode_id: options.episodeId,
      title: options.title,
      position_seconds: Number.isFinite(audio.currentTime)
        ? audio.currentTime
        : 0,
      duration_seconds: Number.isFinite(audio.duration) ? audio.duration : 0,
      player_session_id: activePlayerSessionId,
      client_sequence: clientSequence,
    })
    .catch(() => undefined);
}

function markReadyForUserResume(
  options: PlayOptions,
  set: (state: Partial<AudioState>) => void,
): void {
  set({
    status: "ready_for_tap",
    currentTitle: options.title,
    message:
      "声音已经准备好。可以双指双击播放或暂停，也可以点继续播放准备好的声音。",
  });
}

function markPausedForUserResume(
  options: PlayOptions,
  set: (state: Partial<AudioState>) => void,
): void {
  set({
    status: "ready_for_tap",
    currentTitle: options.title,
    message: "已暂停播放，双指双击即可继续播放。",
  });
}

function resumedPlaybackMessage(title: string): string {
  return `继续播放${title}，双指双击可暂停。`;
}

function preparingMessage(title: string): string {
  return `正在准备播放：${title}，请稍等。准备好后会提示双指双击播放或暂停。`;
}

function shouldProbeStream(streamUrl: string): boolean {
  try {
    const parsed = new URL(streamUrl, window.location.origin);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith("/api/")
    );
  } catch {
    return streamUrl.startsWith("/api/");
  }
}

async function probeStream(streamUrl: string): Promise<void> {
  if (!shouldProbeStream(streamUrl)) {
    return;
  }

  const response = await fetch(streamUrl, {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Range: "bytes=0-0" },
  });

  if (response.status === 401 || response.status === 403) {
    throw new StreamAuthorizationError(response.status);
  }

  if (!response.ok) {
    throw new Error(`stream probe failed: ${response.status}`);
  }

  await response.arrayBuffer().catch(() => undefined);
}

function restorePosition(positionSeconds: number): void {
  if (positionSeconds <= 0 || !Number.isFinite(positionSeconds)) {
    return;
  }

  try {
    audio.currentTime = positionSeconds;
  } catch {
    audio.addEventListener(
      "loadedmetadata",
      () => {
        audio.currentTime = positionSeconds;
      },
      { once: true },
    );
  }
}

async function resolveStreamUrl(
  options: PlayOptions,
  isCurrent: () => boolean = () => true,
): Promise<string | null> {
  const grant = options.grant ? await options.grant() : null;
  if (!isCurrent()) {
    return null;
  }

  const primaryStreamUrl = grant?.stream_url ?? options.streamUrl ?? "";

  try {
    await probeStream(primaryStreamUrl);
    if (!isCurrent()) {
      return null;
    }
    return primaryStreamUrl;
  } catch (error) {
    if (!isCurrent()) {
      return null;
    }

    if (
      !options.fallbackStreamUrl ||
      options.grant ||
      error instanceof StreamAuthorizationError
    ) {
      throw error;
    }

    await probeStream(options.fallbackStreamUrl);
    if (!isCurrent()) {
      return null;
    }
    return options.fallbackStreamUrl;
  }
}

function loadPreparedAudio(streamUrl: string, positionSeconds: number): void {
  audio.pause();
  audio.src = streamUrl;
  audio.preload = "metadata";
  restorePosition(positionSeconds);
}

async function prepareAudio(
  options: PlayOptions,
  positionSeconds: number,
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  const streamUrl = await resolveStreamUrl(options, isCurrent);
  if (streamUrl === null || !isCurrent()) {
    return false;
  }

  loadPreparedAudio(streamUrl, positionSeconds);
  return true;
}

async function prepareAudioWithRetry(
  options: PlayOptions,
  positionSeconds: number,
  set: (state: Partial<AudioState>) => void,
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= STREAM_PREPARE_ATTEMPTS; attempt += 1) {
    try {
      return await prepareAudio(options, positionSeconds, isCurrent);
    } catch (error) {
      if (!isCurrent()) {
        return false;
      }
      if (error instanceof StreamAuthorizationError) {
        throw error;
      }

      lastError = error;
      if (attempt >= STREAM_PREPARE_ATTEMPTS) {
        break;
      }

      set({
        status: "loading",
        currentTitle: options.title,
        message: `播放网络不稳定，正在自动重试第 ${attempt + 1} 次：${options.title}。请稍等。`,
      });
      await delay(RETRY_DELAY_MS * attempt);
    }
  }

  throw lastError;
}

async function startAudio(
  options: PlayOptions,
  set: (state: Partial<AudioState>) => void,
  isCurrent: () => boolean = () => true,
): Promise<void> {
  for (let attempt = 1; attempt <= AUDIO_PLAY_ATTEMPTS; attempt += 1) {
    try {
      await audio.play();
      if (!isCurrent()) {
        return;
      }

      playbackErrorRecoveryCount = 0;
      set({
        status: "playing",
        currentTitle: options.title,
        message: `正在播放：${options.title}`,
      });
      saveProgress(options);
      return;
    } catch (error) {
      if (!isCurrent()) {
        return;
      }

      if (isAutoplayBlocked(error)) {
        markReadyForUserResume(options, set);
        return;
      }

      if (
        attempt < AUDIO_PLAY_ATTEMPTS &&
        isRetryablePlaybackStartError(error)
      ) {
        set({
          status: "loading",
          currentTitle: options.title,
          message: `播放器刚才没有接上，正在自动重试：${options.title}。请稍等。`,
        });
        audio.load();
        await delay(RETRY_DELAY_MS);
        continue;
      }

      set({
        status: "error",
        currentTitle: options.title,
        message:
          "声音暂时没有加载好，请等待几秒后再试。仍不能播放，请返回上一层。",
      });
      return;
    }
  }
}

async function recoverPlayback(
  options: PlayOptions,
  set: (state: Partial<AudioState>) => void,
): Promise<void> {
  const playRequestId = activePlayRequestId;
  if (recoveringPlayRequestId === playRequestId) {
    return;
  }

  recoveringPlayRequestId = playRequestId;
  const positionSeconds = readablePosition();
  saveProgress(options);
  set({
    status: "loading",
    currentTitle: options.title,
    message: "播放连接正在恢复，准备从刚才的位置继续。",
  });

  try {
    const prepared = await prepareAudioWithRetry(
      options,
      positionSeconds,
      set,
      () => isCurrentPlayRequest(playRequestId),
    );
    if (!prepared) {
      return;
    }

    await startAudio(options, set, () => isCurrentPlayRequest(playRequestId));
  } catch {
    if (!isCurrentPlayRequest(playRequestId)) {
      return;
    }

    set({
      status: "error",
      currentTitle: options.title,
      message: "播放连接恢复失败，请返回上一层后重试。",
    });
  } finally {
    if (recoveringPlayRequestId === playRequestId) {
      recoveringPlayRequestId = null;
    }
  }
}

export const useAudioCoordinator = create<AudioState>((set, get) => {
  audio.addEventListener("error", () => {
    if (!activeOptions) {
      return;
    }
    if (playbackErrorRecoveryCount >= PLAYBACK_ERROR_RECOVERY_LIMIT) {
      set({
        status: "error",
        currentTitle: activeOptions.title,
        message: "播放连接多次中断，请返回上一层后重试。",
      });
      return;
    }
    playbackErrorRecoveryCount += 1;
    void recoverPlayback(activeOptions, set);
  });
  audio.addEventListener("pause", () => {
    if (!activeOptions || suppressPauseRecovery || get().status !== "playing") {
      return;
    }
    saveProgress(activeOptions);
    markPausedForUserResume(activeOptions, set);
  });
  audio.addEventListener("play", () => {
    if (!activeOptions || get().status !== "ready_for_tap") {
      return;
    }
    playbackErrorRecoveryCount = 0;
    set({
      status: "playing",
      currentTitle: activeOptions.title,
      message: resumedPlaybackMessage(activeOptions.title),
    });
  });

  function checkpointProgress(): void {
    if (!activeOptions || get().status !== "playing") {
      return;
    }
    saveProgress(activeOptions);
  }

  window.addEventListener("pagehide", checkpointProgress);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      checkpointProgress();
      return;
    }

    if (activeOptions && get().status === "playing" && audio.paused) {
      saveProgress(activeOptions);
      markReadyForUserResume(activeOptions, set);
    }
  });

  return {
    status: "idle",
    currentTitle: "",
    message: "",
    announce(message) {
      set({ message });
    },
    async play(options) {
      const nextPlayKey = playRequestKey(options);
      if (get().status === "loading" && pendingPlayKey === nextPlayKey) {
        set({
          status: "loading",
          currentTitle: options.title,
          message: preparingMessage(options.title),
        });
        return;
      }

      const previousOptions = activeOptions;
      if (previousOptions && get().status === "playing") {
        saveProgress(previousOptions);
      }

      activePlayerSessionId = createPlayerSessionId();
      clientSequence = 0;
      activePlayRequestId += 1;
      const playRequestId = activePlayRequestId;
      playbackErrorRecoveryCount = 0;
      activeOptions = options;
      pendingPlayKey = nextPlayKey;
      set({
        status: "loading",
        currentTitle: options.title,
        message: preparingMessage(options.title),
      });
      try {
        const prepared = await prepareAudioWithRetry(
          options,
          options.startAtSeconds ?? 0,
          set,
          () => isCurrentPlayRequest(playRequestId),
        );
        if (!prepared) {
          return;
        }

        await startAudio(options, set, () =>
          isCurrentPlayRequest(playRequestId),
        );
      } catch (error) {
        if (!isCurrentPlayRequest(playRequestId)) {
          return;
        }

        if (error instanceof StreamAuthorizationError && options.grant) {
          await recoverPlayback(options, set);
          return;
        }

        set({
          status: "error",
          currentTitle: options.title,
          message:
            "声音暂时没有加载好，请等待几秒后再试。仍不能播放，请返回上一层。",
        });
      } finally {
        if (
          isCurrentPlayRequest(playRequestId) &&
          pendingPlayKey === nextPlayKey
        ) {
          pendingPlayKey = null;
        }
      }
    },
    async resumePrepared() {
      if (!activeOptions) {
        return;
      }
      const options = activeOptions;
      const wasReadyForTap = get().status === "ready_for_tap";
      await startAudio(options, set);
      if (wasReadyForTap && get().status === "playing") {
        set({
          currentTitle: options.title,
          message: resumedPlaybackMessage(options.title),
        });
      }
    },
    stopAll() {
      const stoppingOptions = activeOptions;
      if (stoppingOptions && readablePosition() > 0) {
        saveProgress(stoppingOptions);
      }
      activePlayRequestId += 1;
      activeOptions = null;
      pendingPlayKey = null;
      playbackErrorRecoveryCount = 0;
      suppressPauseRecovery = true;
      audio.pause();
      suppressPauseRecovery = false;
      audio.removeAttribute("src");
      audio.load();
      set({
        status: "stopped",
        currentTitle: "",
        message: "已经停止所有 App 声音。",
      });
    },
  };
});

export function playPreparedAudio(): void {
  void useAudioCoordinator.getState().resumePrepared();
}
